<template>
  <div class="slideshow-plugin">
    <div class="d-flex align-items-center mb-4">
      <div>
        <h1 class="mb-1">Inventory Slideshow</h1>
        <p class="text-muted mb-0">
          Create presentations of your inventory items with customizable transitions and timing.
        </p>
      </div>
    </div>

    <div v-if="loaded">
      <!-- Slideshow Button with Controls -->
      <inventory-slideshow-button
        class="mb-4"
        :selected-items="selectedItems"
        @on-visible="selectVisible"
        @on-clear="clearSelected"
      />

      <!-- Search -->
      <input
        type="search"
        v-model="searchTerm"
        placeholder="Search Title"
        class="form-control mb-3"
      >

      <!-- Inventory List with Selection -->
      <div class="alert alert-info" v-if="filteredInventory.length === 0">
        <i class="bi bi-info-circle"></i> No inventory items found.
        <router-link :to="`/store/${storeKey}/inventory`">Go to Inventory</router-link> to add items.
      </div>

      <div v-else>
        <p class="text-muted small mb-3">
          Select items below to include in your slideshow. Click the eye icon to select the first 35 visible items.
        </p>

        <div v-for="inv in filteredInventory" :key="inv.id" class="mb-3">
          <inventory-item
            :item="inv"
            :show-select-box="true"
            :selected="selectedList.indexOf(inv.id) >= 0"
            @on-selected="(checked) => updateList(inv.id, checked)"
          />
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading inventory...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import InventorySlideshowButton from '../components/InventorySlideshowButton.vue'
import InventoryItem from '@/app/components/inventory/InventoryItem.vue'

const store = useStore()
const route = useRoute()

const loaded = ref(false)
const searchTerm = ref('')
const selectedList = ref([])

const storeKey = computed(() => route.params.storeKey)
const inventory = computed(() => store.state.eshop.inventory || [])
const selectedItems = computed(() =>
  inventory.value.filter(item => selectedList.value.includes(item.id))
)
const filteredInventory = computed(() => {
  if (!searchTerm.value) return inventory.value

  const search = searchTerm.value.toLowerCase()
  return inventory.value.filter(item =>
    item.title?.toLowerCase().includes(search) ||
    item.SKU?.toLowerCase().includes(search)
  )
})

function clearSelected() {
  selectedList.value = []
}

function selectVisible() {
  clearSelected()
  selectedList.value = filteredInventory.value.slice(0, 35).map(i => i.id)
}

function updateList(id, selected) {
  selectedList.value = selectedList.value.filter(l => l !== id)
  if (selected) selectedList.value = [...selectedList.value, id]
}

// Watch for search term changes
let searchTimeout
watch(searchTerm, () => {
  clearTimeout(searchTimeout)
  // Don't clear selection when searching
})

// Fetch inventory on mount
store.dispatch('eshop/getInventory').then(() => {
  loaded.value = true
})
</script>

<style scoped>
.slideshow-plugin {
  max-width: 1200px;
}
</style>
